'use client'

import { createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface IImageMediaOverlayItem {
    type: 'image'
    src: string
    alt?: string
}

export interface IVideoMediaOverlayItem {
    type: 'video'
    src: string
    alt?: string
    /** Stage aspect for the overlay. Defaults to 'portrait' (9:16) for creative-ad clips.
     *  Use 'landscape' for 16:9 films (e.g. the launch video). */
    aspect?: 'portrait' | 'landscape'
}

export interface IYouTubeMediaOverlayItem {
    type: 'youtube'
    embedId: string
    alt?: string
}

export type IMediaOverlayItem = IImageMediaOverlayItem | IVideoMediaOverlayItem | IYouTubeMediaOverlayItem

export interface IOpenLandingMediaOverlayPayload {
    items: IMediaOverlayItem[]
    initialIndex?: number
}

interface ILandingMediaOverlayContext {
    isOpen: boolean
    items: IMediaOverlayItem[]
    initialIndex: number
    open: (payload: IOpenLandingMediaOverlayPayload) => void
    close: () => void
}

const LandingMediaOverlayContext = createContext<ILandingMediaOverlayContext | null>(null)

export function LandingMediaOverlayProvider({ children }: { children: React.ReactNode }) {
    const [isOpen, setIsOpen] = useState(false)
    const [items, setItems] = useState<IMediaOverlayItem[]>([])
    const [initialIndex, setInitialIndex] = useState(0)

    const open = useCallback((payload: IOpenLandingMediaOverlayPayload) => {
        if (payload.items.length === 0) return

        const requestedIndex = payload.initialIndex ?? 0
        const boundedIndex = Math.max(0, Math.min(requestedIndex, payload.items.length - 1))

        setItems(payload.items)
        setInitialIndex(boundedIndex)
        setIsOpen(true)
    }, [])

    const close = useCallback(() => {
        setIsOpen(false)
    }, [])

    const value = useMemo(
        () => ({
            isOpen,
            items,
            initialIndex,
            open,
            close,
        }),
        [close, initialIndex, isOpen, items, open]
    )

    return <LandingMediaOverlayContext.Provider value={value}>{children}</LandingMediaOverlayContext.Provider>
}

export function useLandingMediaOverlay() {
    const context = useContext(LandingMediaOverlayContext)

    if (!context) {
        throw new Error('useLandingMediaOverlay must be used within a LandingMediaOverlayProvider')
    }

    return context
}
